/**
 * Central semiconductor FAB color system — reference image is source of truth.
 * Hierarchy: dark blue floor → metallic blue equipment → graphite → smoked glass → FOUPs → cyan accents.
 */
export const FAB_COLORS = {
  /** Primary metallic blue equipment panels (replaces light gray/white). */
  bodyWhite: '#356890',
  bodyBright: '#4A82AE',
  bodyLight: '#284868',
  bodySilver: '#6098B8',
  bodyHighlight: '#5C96BE',
  bodyWarm: '#3A6080',

  graphite: '#202A32',
  darkGraphite: '#111820',
  blueGraphite: '#182833',
  frameInner: '#0F1820',

  metal: '#6E8FA8',
  brushedMetal: '#5A90B0',
  stainless: '#5284A8',

  /** Dark smoked blue-black glass — chamber accents. */
  glass: '#071923',
  glassBlue: '#0A1E2C',
  blueGlass: '#0F2430',
  blueGlassTint: '#142A38',
  blueMetal: '#1E6AA8',
  /** Cleanroom white transparent enclosure glass. */
  whiteGlass: '#EEF2F6',
  whiteGlassTint: '#F5F8FA',
  /** Opaque white crane aisle rail + FOUP placement deck inside stockers. */
  placementTray: '#EEF2F6',
  mechanismRod: '#F8FAFC',
  /** Legacy alias — now white glass for enclosure shells. */
  clearGlass: '#E8EEF4',
  /** Pale cyan-blue transparent glass — stocker shelves and sky-blue accents. */
  lightBlueGlass: '#6EC4E8',
  lightBlueGlassBright: '#9ADCF5',
  /** Sky blue — stocker frame, shelf decks, and shelf-zone back panels. */
  skyBlue: '#87CEEB',
  skyBlueBright: '#B0E2FF',
  skyBlueFrame: '#6EC4E8',
  /** Pista / pistachio green — stocker interior shelf glass. */
  pistaGreenGlass: '#9FD38A',
  pistaGreenGlassBright: '#B8E4A6',
  /** Navy blue transparent glass — stocker enclosure shells. */
  navyGlass: '#284868',
  navyGlassBright: '#356890',
  navyGlassDeep: '#1A3450',
  /** Dark baby pink tinted glass — process tool chamber window. */
  babyPinkGlass: '#C4A8B0',
  babyPinkGlassBright: '#D9CFD2',
  babyPinkGlassDeep: '#A88890',

  cyan: '#00BFFF',
  cyanSoft: '#28D7FF',

  green: '#21E38A',
  amber: '#FFD43B',
  red: '#FF4545',

  /** Inner fab slab — gray cleanroom floor. */
  floor: '#94A3B8',
  floorHighlight: '#DCE4EC',
  /** Dark blue tile tones — navy base, lighter blue veins, glass shine. */
  floorTileAquamarine: '#345876',
  floorTileShine: '#5A9AB8',
  floorTileGlassBlue: '#2A5070',
  floorTilePistaGreen: '#3A6078',
  floorTileTeal: '#1E4560',
  /** Thin black mosaic grid lines. */
  floorGrout: '#141414',
  floorSeam: '#141414',
  /** Warm muted industrial yellow — physical, non-emissive. */
  floorLane: '#E5B72C',
  /** Outer scene pad / background wash — clean white surround. */
  floorSurround: '#FFFFFF',

  railOuter: '#EEF2F6',
  railHighlight: '#F8FAFC',
  railDark: '#181A1D',
  railWeb: '#D4DCE4',
  railInterior: '#14181C',

  foupBody: '#0F2B5C',
  foupTop: '#081730',
  foupInterface: '#0A1C36',

  loadPortBody: '#4A82AE',
  loadPortRecess: '#202A32',
  loadPortInterface: '#111820',

  /** Recessed air-return grilles — dark graphite metal, not pure black. */
  grillePanel: '#565F66',
  grilleFrame: '#7A868E',
  grilleInterior: '#394248',
  grilleHole: '#1E262C',
} as const;

export const FAB_BODY_SHADING = {
  /** Metallic blue body panels — stockers, process tools, load ports. */
  white: { metalness: 0.58, roughness: 0.28, envMapIntensity: 0.88 },
  bright: { metalness: 0.54, roughness: 0.24, envMapIntensity: 0.92 },
  lightGray: { metalness: 0.52, roughness: 0.3, envMapIntensity: 0.84 },
  silver: { metalness: 0.52, roughness: 0.32, envMapIntensity: 0.8 },
  graphite: { metalness: 0.46, roughness: 0.42, envMapIntensity: 0.68 },
  darkGraphite: { metalness: 0.4, roughness: 0.46, envMapIntensity: 0.6 },
  glassDistant: { metalness: 0.08, roughness: 0.18, envMapIntensity: 0.72 },
  blueGlass: { metalness: 0.1, roughness: 0.16, envMapIntensity: 0.74 },
  clearGlass: { metalness: 0.08, roughness: 0.18, envMapIntensity: 0.72 },
} as const;

export const FAB_LED_INTENSITY = {
  /** Small accent only — do not dominate stocker/equipment silhouette. */
  cyanEdge: 1.35,
  cyanSoft: 0.85,
  green: 1.4,
  amber: 1.2,
  red: 1.6,
} as const;

export type FabColorKey = keyof typeof FAB_COLORS;
